import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { api } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import Modal from '../../components/common/Modal';
import ConfirmModal from '../../components/common/ConfirmModal';
import { channelStatusLabel, syncStatusLabel } from '../../utils/propertyStatus';

const Container = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const Title = styled.h1`
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const TabsContainer = styled.div`
  display: flex;
  border-bottom: 2px solid ${({ theme }) => theme.colors.border};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  border: none;
  background: none;
  cursor: pointer;
  font-weight: 500;
  border-bottom: 3px solid transparent;
  transition: all 0.3s ease;
  
  ${({ $active, theme }) => $active ? `
    color: ${theme.colors.primary};
    border-bottom-color: ${theme.colors.primary};
  ` : `
    color: ${theme.colors.text.secondary};
    &:hover {
      color: ${theme.colors.text.primary};
    }
  `}
`;

const Section = styled.div`
  background-color: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  box-shadow: ${({ theme }) => theme.shadows.sm};
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ConfigStatus = styled.div<{ $connected: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  
  ${({ $connected }) => $connected ? `
    background-color: #d4edda;
    color: #155724;
  ` : `
    background-color: #f8d7da;
    color: #721c24;
  `}
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' | 'warning' }>`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-right: ${({ theme }) => theme.spacing.sm};
  
  ${({ $variant, theme }) => {
    switch ($variant) {
      case 'danger':
        return `
          background-color: #dc3545;
          color: white;
          &:hover { background-color: #c82333; }
        `;
      case 'warning':
        return `
          background-color: #ffc107;
          color: #212529;
          &:hover { background-color: #e0a800; }
        `;
      case 'secondary':
        return `
          background-color: ${theme.colors.background};
          color: ${theme.colors.text.primary};
          border: 1px solid ${theme.colors.border};
          &:hover { background-color: ${theme.colors.border}; }
        `;
      default:
        return `
          background-color: ${theme.colors.primary};
          color: white;
          &:hover { background-color: ${theme.colors.secondary}; }
        `;
    }
  }}
  
  &:disabled {
    background-color: ${({ theme }) => theme.colors.border};
    cursor: not-allowed;
  }
`;

const FormGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const Label = styled.label`
  display: block;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Input = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ProgressContainer = styled.div`
  margin-top: ${({ theme }) => theme.spacing.md};
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 20px;
  background-color: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  overflow: hidden;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const ProgressFill = styled.div<{ percentage: number }>`
  height: 100%;
  background: linear-gradient(90deg, #3b82f6, #8b5cf6);
  width: ${({ percentage }) => percentage}%;
  transition: width 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.875rem;
  font-weight: 600;
`;

const ProgressInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const ProgressItem = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.sm};
  background-color: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
`;

const ProgressLabel = styled.div`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const ProgressValue = styled.div<{ color?: string }>`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${({ color, theme }) => color || theme.colors.text.primary};
`;

const SyncHealthAlert = styled.div<{ $severity: 'healthy' | 'warning' | 'danger' }>`
  padding: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  border: 1px solid ${({ $severity }) => {
    if ($severity === 'danger') return '#fecaca';
    if ($severity === 'warning') return '#fde68a';
    return '#bbf7d0';
  }};
  border-left-width: 4px;
  background: ${({ $severity }) => {
    if ($severity === 'danger') return '#fef2f2';
    if ($severity === 'warning') return '#fffbeb';
    return '#f0fdf4';
  }};
  color: ${({ $severity }) => {
    if ($severity === 'danger') return '#991b1b';
    if ($severity === 'warning') return '#92400e';
    return '#166534';
  }};
`;

const SyncHealthHeading = styled.div`
  font-weight: 700;
  margin-bottom: 0.35rem;
`;

const SyncHealthMeta = styled.div`
  font-size: 0.875rem;
  line-height: 1.5;
`;

const SyncHealthIssues = styled.ul`
  margin: 0.75rem 0 0 1.25rem;
  padding: 0;
  font-size: 0.875rem;
  line-height: 1.5;
`;

const LogContainer = styled.div`
  background-color: #1a1a1a;
  color: #00ff00;
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-family: 'Courier New', monospace;
  font-size: 0.875rem;
  height: 200px;
  overflow-y: auto;
  margin-top: ${({ theme }) => theme.spacing.md};
`;

const JobsTable = styled.div`
  margin-top: ${({ theme }) => theme.spacing.md};
`;

const JobRow = styled.div`
  display: grid;
  grid-template-columns: 80px 120px 1fr 100px 120px 100px;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.sm};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  align-items: center;
`;

const JobHeader = styled(JobRow)`
  font-weight: 600;
  background-color: ${({ theme }) => theme.colors.background};
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  
  ${({ status }) => {
    switch (status) {
      case 'completed':
        return 'background-color: #d4edda; color: #155724;';
      case 'running':
        return 'background-color: #cce5ff; color: #0066cc;';
      case 'failed':
        return 'background-color: #f8d7da; color: #721c24;';
      case 'pending_confirmation':
      case 'conflict':
        return 'background-color: #fff3cd; color: #856404;';
      case 'synced':
        return 'background-color: #d4edda; color: #155724;';
      case 'cancelled':
        return 'background-color: #fff3cd; color: #856404;';
      default:
        return 'background-color: #e2e3e5; color: #495057;';
    }
  }}
`;

const ImportStatusCard = styled.div<{ isActive: boolean }>`
  position: fixed;
  top: 20px;
  right: 20px;
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  padding: ${({ theme }) => theme.spacing.md};
  z-index: 1000;
  min-width: 300px;
  border-left: 4px solid ${({ isActive }) => isActive ? '#3b82f6' : '#10b981'};
  
  ${({ isActive }) => !isActive && `
    cursor: pointer;
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 10px 25px rgba(0,0,0,0.15);
    }
  `}
`;

interface ImportJob {
  id: number;
  status: string;
  completion_percentage?: number;
  total_properties?: number;
  created_properties?: number;
  updated_properties?: number;
  failed_properties?: number;
  created_at: string;
  start_time?: string;
  end_time?: string;
}

interface ImportProgress {
  job_id: number;
  status: string;
  progress_percentage: number;
  current_property?: string;
  total_properties: number;
  processed_properties: number;
  imported_properties: number;
  updated_properties: number;
  skipped_properties: number;
  failed_properties: number;
  total_images: number;
  downloaded_images: number;
  failed_images: number;
  log_messages: string[];
  started_at?: string;
  estimated_completion?: string;
}

interface SyncHealthIssue {
  tipo: string;
  detalhe: string;
  [key: string]: unknown;
}

interface SyncReconciliation {
  inbound_enabled: boolean;
  last_reconciled_at?: string | null;
  interval_minutes: number;
  age_minutes: number | null;
  stale_after_minutes: number;
  stale: boolean;
}

interface SyncOverview {
  settings: {
    sync_enabled: boolean;
    inbound_enabled: boolean;
    outbound_enabled: boolean;
    active_listing_limit: number;
    sync_interval_minutes: number;
    new_remote_site_status: string;
    new_local_channel_status: string;
    conflict_strategy: string;
    last_reconciled_at?: string | null;
  };
  quota: { limit: number; active: number; reserved: number; available: number };
  total_publications: number;
  conflicts: number;
  errors: number;
  pending_confirmation?: number;
  pending_events: number;
  health?: {
    status: 'healthy' | 'degraded';
    healthy: boolean;
    reconciliation: SyncReconciliation;
    issues: SyncHealthIssue[];
  };
}

const AdminCanalPro: React.FC = () => {
  const { showNotification } = useNotification();
  const [activeTab, setActiveTab] = useState('import');
  const [config, setConfig] = useState({
    user_email: '',
    password: '',
    device_id: '',
    publisher_id: '',
    contract_id: '',
    sync_enabled: false,
    inbound_enabled: true,
    outbound_enabled: false,
    active_listing_limit: 25,
    sync_interval_minutes: 15,
    new_remote_site_status: 'hidden',
    new_local_channel_status: 'DRAFT',
    conflict_strategy: 'manual'
  });
  const [configExists, setConfigExists] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [currentJob, setCurrentJob] = useState<ImportProgress | null>(null);
  const [recentJobs, setRecentJobs] = useState<ImportJob[]>([]);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [showClearModal, setShowClearModal] = useState(false);
  const [testResult, setTestResult] = useState<any>(null);
  const [syncOverview, setSyncOverview] = useState<SyncOverview | null>(null);
  const [syncPublications, setSyncPublications] = useState<any[]>([]);
  const reconcileRefreshTimeoutRef = useRef<number | null>(null);

  // Persistir estado da importação no localStorage
  const saveImportState = useCallback((jobId: number | null, importing: boolean) => {
    localStorage.setItem('canalProImportState', JSON.stringify({ jobId, importing }));
  }, []);

  const loadImportState = useCallback(() => {
    const saved = localStorage.getItem('canalProImportState');
    if (saved) {
      const { jobId, importing } = JSON.parse(saved);
      if (importing && jobId) {
        setIsImporting(true);
        startProgressPolling(jobId);
      }
    }
  }, []);

  const clearImportState = useCallback(() => {
    localStorage.removeItem('canalProImportState');
  }, []);

  const fetchConfig = useCallback(async () => {
    try {
      const response = await api.get('/canal-pro/config');
      setConfig(response.data);
      setConfigExists(true);
    } catch (error: any) {
      if (error.response?.status !== 404) {
        console.error('Erro ao carregar configuração:', error);
      }
      setConfigExists(false);
    }
  }, []);

  const fetchRecentJobs = useCallback(async () => {
    try {
      const response = await api.get('/canal-pro/import/jobs?limit=10');
      setRecentJobs(response.data);
      return response.data;
    } catch (error) {
      console.error('Erro ao carregar jobs:', error);
      return [];
    }
  }, []);

  const fetchSyncOverview = useCallback(async () => {
    const [overviewResult, publicationsResult] = await Promise.allSettled([
      api.get<SyncOverview>('/canal-pro/sync/overview'),
      api.get('/canal-pro/sync/publications?limit=500'),
    ]);

    if (overviewResult.status === 'fulfilled') {
      setSyncOverview(overviewResult.value.data);
    } else {
      const error: any = overviewResult.reason;
      if (error.response?.status === 404) setSyncOverview(null);
      else console.error('Erro ao carregar saúde da sincronização:', error);
    }

    if (publicationsResult.status === 'fulfilled') {
      setSyncPublications(publicationsResult.value.data.items || []);
    } else {
      const error: any = publicationsResult.reason;
      if (error.response?.status !== 404) {
        console.error('Erro ao carregar publicações sincronizadas:', error);
      }
    }
  }, []);

  const handleSaveSyncSettings = async () => {
    setIsLoading(true);
    try {
      await api.patch('/canal-pro/sync/settings', {
        sync_enabled: config.sync_enabled,
        inbound_enabled: config.inbound_enabled,
        outbound_enabled: config.outbound_enabled,
        active_listing_limit: Number(config.active_listing_limit),
        sync_interval_minutes: Number(config.sync_interval_minutes),
        new_remote_site_status: config.new_remote_site_status,
        new_local_channel_status: config.new_local_channel_status,
        conflict_strategy: config.conflict_strategy
      });
      await fetchSyncOverview();
      showNotification('Sincronização configurada com sucesso!', 'success');
    } catch (error: any) {
      showNotification(error.response?.data?.detail || 'Erro ao salvar sincronização', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReconcile = async () => {
    try {
      await api.post('/canal-pro/sync/reconcile');
      showNotification('Reconciliação enfileirada', 'success');
      if (reconcileRefreshTimeoutRef.current !== null) {
        window.clearTimeout(reconcileRefreshTimeoutRef.current);
      }
      reconcileRefreshTimeoutRef.current = window.setTimeout(() => {
        fetchSyncOverview();
        reconcileRefreshTimeoutRef.current = null;
      }, 3000);
    } catch (error: any) {
      showNotification(error.response?.data?.detail || 'Erro ao reconciliar', 'error');
    }
  };

  const handleChannelStatus = async (propertyId: string, desiredStatus: string) => {
    try {
      await api.patch(`/canal-pro/sync/properties/${propertyId}/status`, { desired_status: desiredStatus });
      await fetchSyncOverview();
      showNotification(`Status ${desiredStatus} enfileirado`, 'success');
    } catch (error: any) {
      showNotification(error.response?.data?.detail || 'Erro ao alterar status do canal', 'error');
    }
  };

  const handleConflictResolution = async (publicationId: string, resolution: 'local' | 'remote') => {
    try {
      await api.post(`/canal-pro/sync/conflicts/${publicationId}/resolve`, { resolution });
      await fetchSyncOverview();
      showNotification(
        resolution === 'local' ? 'Alteração do site escolhida' : 'Alteração do Canal Pro escolhida',
        'success'
      );
    } catch (error: any) {
      showNotification(error.response?.data?.detail || 'Erro ao resolver conflito', 'error');
    }
  };

  const refreshJobs = useCallback(async () => {
    const jobs = await fetchRecentJobs();
    return jobs;
  }, [fetchRecentJobs]);

  const startProgressPolling = useCallback((jobId: number) => {
    const pollProgress = async () => {
      try {
        const response = await api.get(`/canal-pro/import/progress/${jobId}`);
        const progress: ImportProgress = response.data;
        setCurrentJob(progress);
        
        if (progress.status === 'completed' || progress.status === 'failed' || progress.status === 'cancelled') {
          setIsImporting(false);
          setCurrentJob(null);
          clearImportState();
          refreshJobs();
          showNotification(
            progress.status === 'completed' 
              ? 'Importação concluída com sucesso!' 
              : `Importação ${progress.status === 'failed' ? 'falhou' : 'foi cancelada'}`,
            progress.status === 'completed' ? 'success' : 'error'
          );
          return;
        }
        
        // Continuar polling se ainda está em execução
        setTimeout(pollProgress, 2000);
      } catch (error) {
        console.error('Erro ao buscar progresso:', error);
        setIsImporting(false);
        setCurrentJob(null);
        clearImportState();
      }
    };
    
    pollProgress();
  }, [showNotification, clearImportState, refreshJobs]);

  // Inicialização uma única vez
  useEffect(() => {
    const initializeData = async () => {
      // Limpar estado corrupto
      clearImportState();
      
      await fetchConfig();
      const jobs = await fetchRecentJobs();
      await fetchSyncOverview();
      
      // Verificar se há job em execução
      const runningJob = jobs.find((job: ImportJob) => 
        job.status === 'running' || job.status === 'pending'
      );
      
      if (runningJob) {
        setIsImporting(true);
        startProgressPolling(runningJob.id);
        saveImportState(runningJob.id, true);
      }
    };
    
    initializeData();
  }, []); // Array vazio - executa apenas uma vez

  useEffect(() => {
    if (activeTab !== 'sync') return undefined;

    fetchSyncOverview();
    const pollingId = window.setInterval(fetchSyncOverview, 45000);
    return () => window.clearInterval(pollingId);
  }, [activeTab, fetchSyncOverview]);

  useEffect(() => () => {
    if (reconcileRefreshTimeoutRef.current !== null) {
      window.clearTimeout(reconcileRefreshTimeoutRef.current);
    }
  }, []);

  const handleSaveConfig = async () => {
    setIsLoading(true);
    try {
      await api.post('/canal-pro/config', config);
      setConfigExists(true);
      setShowConfigModal(false);
      showNotification('Configuração salva com sucesso!', 'success');
    } catch (error) {
      console.error('Erro ao salvar configuração:', error);
      showNotification('Erro ao salvar configuração', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestConnection = async () => {
    setIsLoading(true);
    try {
      const response = await api.post('/canal-pro/test-connection', config);
      setTestResult(response.data);
      setShowTestModal(true);
    } catch (error) {
      console.error('Erro ao testar conexão:', error);
      setTestResult({
        success: false,
        message: 'Erro ao testar conexão',
        properties_count: 0
      });
      setShowTestModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartImport = async () => {
    try {
      const response = await api.post('/canal-pro/import');
      const result = response.data;
      
      if (result.success && result.job_id) {
        setIsImporting(true);
        saveImportState(result.job_id, true);
        startProgressPolling(result.job_id);
        showNotification('Importação iniciada com sucesso!', 'success');
      } else {
        showNotification(result.message || 'Erro ao iniciar importação', 'error');
      }
    } catch (error: any) {
      console.error('Erro ao iniciar importação:', error);
      showNotification(
        error.response?.data?.detail || 'Erro ao iniciar importação', 
        'error'
      );
    }
  };

  const handleCancelImport = async () => {
    if (!currentJob) return;
    
    try {
      await api.post(`/canal-pro/import/cancel/${currentJob.job_id}`);
      showNotification('Importação cancelada', 'success');
      setIsImporting(false);
      setCurrentJob(null);
      clearImportState();
      refreshJobs();
    } catch (error) {
      console.error('Erro ao cancelar importação:', error);
      showNotification('Erro ao cancelar importação', 'error');
    }
  };

  const handleCancelSpecificJob = async (jobId: number) => {
    try {
      await api.post(`/canal-pro/import/cancel/${jobId}`);
      showNotification('Job cancelado com sucesso', 'success');
      
      // Se é o job atual em execução, limpar estado
      if (currentJob && currentJob.job_id === jobId) {
        setIsImporting(false);
        setCurrentJob(null);
        clearImportState();
      }
      
      refreshJobs();
    } catch (error) {
      console.error('Erro ao cancelar job:', error);
      showNotification('Erro ao cancelar job', 'error');
    }
  };

  const handleRestartJob = async (jobId: number) => {
    try {
      const response = await api.post(`/canal-pro/import/restart/${jobId}`);
      showNotification('Job reiniciado com sucesso', 'success');
      
      // Atualizar estado para o novo job
      if (response.data.new_job_id) {
        setIsImporting(true);
        setCurrentJob({
          job_id: response.data.new_job_id,
          status: 'running',
          progress_percentage: 0,
          total_properties: 0,
          processed_properties: 0,
          imported_properties: 0,
          updated_properties: 0,
          skipped_properties: 0,
          failed_properties: 0,
          total_images: 0,
          downloaded_images: 0,
          failed_images: 0,
          log_messages: ['Job reiniciado'],
          current_property: undefined
        });
        
        // Iniciar polling do novo job
        startProgressPolling(response.data.new_job_id);
      }
      
      refreshJobs();
    } catch (error) {
      console.error('Erro ao reiniciar job:', error);
      showNotification('Erro ao reiniciar job', 'error');
    }
  };

  const handleClearDatabase = async () => {
    try {
      await api.post('/canal-pro/clear-data');
      showNotification('Base de dados limpa com sucesso!', 'success');
      setShowClearModal(false);
      refreshJobs();
    } catch (error) {
      console.error('Erro ao limpar base:', error);
      showNotification('Erro ao limpar base de dados', 'error');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const formatReconciliationAge = (ageMinutes: number | null) => {
    if (ageMinutes === null) return 'sem execução registrada';
    if (ageMinutes < 1) return 'há menos de 1 minuto';
    if (ageMinutes < 60) return `há ${ageMinutes} min`;
    if (ageMinutes < 1440) {
      const hours = Math.floor(ageMinutes / 60);
      const minutes = ageMinutes % 60;
      return `há ${hours}h${minutes ? ` ${minutes}min` : ''}`;
    }
    const days = Math.floor(ageMinutes / 1440);
    const remainingHours = Math.floor((ageMinutes % 1440) / 60);
    return `há ${days} ${days === 1 ? 'dia' : 'dias'}${remainingHours ? ` e ${remainingHours}h` : ''}`;
  };

  const renderSyncHealth = () => {
    if (!syncOverview?.health) return null;

    const { health } = syncOverview;
    const reconciliation = health.reconciliation;
    const pendingConfirmation = syncOverview.pending_confirmation || 0;
    const severity = !health.healthy ? 'danger' : pendingConfirmation > 0 ? 'warning' : 'healthy';
    const heading = !health.healthy
      ? '⚠️ Sincronização requer atenção'
      : pendingConfirmation > 0
        ? '⏳ Há confirmações pendentes'
        : '✅ Sincronização saudável';

    return (
      <SyncHealthAlert $severity={severity} role={!health.healthy ? 'alert' : 'status'}>
        <SyncHealthHeading>{heading}</SyncHealthHeading>
        <SyncHealthMeta>
          {reconciliation.inbound_enabled ? (
            <>
              <div>
                <strong>Última reconciliação:</strong>{' '}
                {reconciliation.last_reconciled_at
                  ? `${formatDate(reconciliation.last_reconciled_at)} (${formatReconciliationAge(reconciliation.age_minutes)})`
                  : 'nunca executada'}
              </div>
              <div>
                <strong>Recência:</strong>{' '}
                {reconciliation.stale
                  ? `atrasada; o limite de segurança é ${reconciliation.stale_after_minutes} min`
                  : `em dia; alerta após ${reconciliation.stale_after_minutes} min sem reconciliar`}
                {' '}· intervalo configurado: {reconciliation.interval_minutes} min
              </div>
            </>
          ) : (
            <div>O recebimento automático de alterações do Canal Pro está desabilitado.</div>
          )}
          {pendingConfirmation > 0 && (
            <div>
              <strong>Confirmações pendentes:</strong> {pendingConfirmation}{' '}
              {pendingConfirmation === 1 ? 'publicação aguarda' : 'publicações aguardam'} confirmação do estado remoto.
            </div>
          )}
        </SyncHealthMeta>
        {health.issues.length > 0 && (
          <SyncHealthIssues>
            {health.issues.map((issue, index) => (
              <li key={`${issue.tipo}-${index}`}>{issue.detalhe}</li>
            ))}
          </SyncHealthIssues>
        )}
      </SyncHealthAlert>
    );
  };

  const getStatusLabel = (status: string) => {
    const labels: { [key: string]: string } = {
      pending: 'Pendente',
      running: 'Em Execução',
      completed: 'Concluída',
      failed: 'Falhou',
      cancelled: 'Cancelada'
    };
    return labels[status] || status;
  };

  const renderImportTab = () => (
    <>
      {/* Status da Configuração */}
      <Section>
        <SectionTitle>Status da Configuração</SectionTitle>
        <ConfigStatus $connected={configExists}>
          {configExists ? (
            <>
              <span>✅</span>
              <div>
                <strong>Configuração ativa</strong>
                <div style={{ fontSize: '0.875rem', opacity: 0.8 }}>
                  Email: {config.user_email}
                </div>
              </div>
            </>
          ) : (
            <>
              <span>❌</span>
              <div>
                <strong>Configuração necessária</strong>
                <div style={{ fontSize: '0.875rem', opacity: 0.8 }}>
                  Configure suas credenciais do CanalPro
                </div>
              </div>
            </>
          )}
        </ConfigStatus>
        
        <Button onClick={() => setShowConfigModal(true)}>
          {configExists ? '⚙️ Editar Configuração' : '🔧 Configurar CanalPro'}
        </Button>
        
        {configExists && (
          <Button $variant="secondary" onClick={handleTestConnection} disabled={isLoading}>
            🔍 Testar Conexão
          </Button>
        )}
      </Section>

      {/* Importação */}
      <Section>
        <SectionTitle>Importação de Propriedades</SectionTitle>
        
        {!configExists ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
            Configure suas credenciais do CanalPro primeiro
          </div>
        ) : !isImporting ? (
          <div>
            <Button onClick={handleStartImport}>
              🚀 Iniciar Importação
            </Button>
            <Button $variant="warning" onClick={() => setShowClearModal(true)}>
              🗑️ Limpar Base (Dev)
            </Button>
          </div>
        ) : (
          <div>
            <Button $variant="danger" onClick={handleCancelImport}>
              ❌ Cancelar Importação
            </Button>
            
            {currentJob && (
              <ProgressContainer>
                <ProgressBar>
                  <ProgressFill percentage={currentJob.progress_percentage}>
                    {currentJob.progress_percentage.toFixed(1)}%
                  </ProgressFill>
                </ProgressBar>
                
                <ProgressInfo>
                  <ProgressItem>
                    <ProgressLabel>Total</ProgressLabel>
                    <ProgressValue>{currentJob.total_properties}</ProgressValue>
                  </ProgressItem>
                  <ProgressItem>
                    <ProgressLabel>Processadas</ProgressLabel>
                    <ProgressValue>{currentJob.processed_properties}</ProgressValue>
                  </ProgressItem>
                  <ProgressItem>
                    <ProgressLabel>Criadas</ProgressLabel>
                    <ProgressValue color="#10b981">{currentJob.imported_properties}</ProgressValue>
                  </ProgressItem>
                  <ProgressItem>
                    <ProgressLabel>Atualizadas</ProgressLabel>
                    <ProgressValue color="#3b82f6">{currentJob.updated_properties}</ProgressValue>
                  </ProgressItem>
                  <ProgressItem>
                    <ProgressLabel>Puladas</ProgressLabel>
                    <ProgressValue color="#f59e0b">{currentJob.skipped_properties}</ProgressValue>
                  </ProgressItem>
                  <ProgressItem>
                    <ProgressLabel>Erros</ProgressLabel>
                    <ProgressValue color="#ef4444">{currentJob.failed_properties}</ProgressValue>
                  </ProgressItem>
                  <ProgressItem>
                    <ProgressLabel>Imagens</ProgressLabel>
                    <ProgressValue color="#8b5cf6">{currentJob.downloaded_images}/{currentJob.total_images}</ProgressValue>
                  </ProgressItem>
                </ProgressInfo>
                
                {currentJob.current_property && (
                  <div style={{ marginBottom: '1rem', fontSize: '0.875rem', color: '#666' }}>
                    <strong>Processando:</strong> {currentJob.current_property}
                  </div>
                )}
                
                {currentJob.log_messages.length > 0 && (
                  <LogContainer>
                    {currentJob.log_messages.map((log, index) => (
                      <div key={index}>{log}</div>
                    ))}
                  </LogContainer>
                )}
              </ProgressContainer>
            )}
          </div>
        )}
      </Section>
    </>
  );

  const renderHistoryTab = () => (
    <Section>
      <SectionTitle>Histórico de Importações</SectionTitle>
      
      {recentJobs.length > 0 ? (
        <JobsTable>
          <JobHeader>
            <div>ID</div>
            <div>Status</div>
            <div>Progresso</div>
            <div>Criadas</div>
            <div>Atualizadas</div>
            <div>Falharam</div>
            <div>Total</div>
            <div>Iniciado</div>
            <div>Finalizado</div>
            <div>Ações</div>
          </JobHeader>
          
          {recentJobs.map((job) => (
            <JobRow key={job.id}>
              <div>#{job.id}</div>
              <div>
                <StatusBadge status={job.status}>
                  {getStatusLabel(job.status)}
                </StatusBadge>
              </div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span>{(job.completion_percentage || 0).toFixed(1)}%</span>
                  <div style={{ 
                    width: '60px', 
                    height: '6px', 
                    backgroundColor: '#e9ecef', 
                    borderRadius: '3px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${job.completion_percentage || 0}%`,
                      height: '100%',
                      backgroundColor: job.status === 'completed' ? '#28a745' : 
                                     job.status === 'failed' ? '#dc3545' : '#007bff',
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </div>
              </div>
              <div style={{ color: '#28a745', fontWeight: '600' }}>
                {job.created_properties || 0}
              </div>
              <div style={{ color: '#ffc107', fontWeight: '600' }}>
                {job.updated_properties || 0}
              </div>
              <div style={{ color: '#dc3545', fontWeight: '600' }}>
                {job.failed_properties || 0}
              </div>
              <div style={{ fontWeight: '600' }}>
                {job.total_properties || 0}
              </div>
              <div style={{ fontSize: '0.875rem' }}>
                {job.start_time ? formatDate(job.start_time) : formatDate(job.created_at)}
              </div>
              <div style={{ fontSize: '0.875rem' }}>
                {job.end_time ? formatDate(job.end_time) : '-'}
              </div>
              <div>
                {job.status === 'running' && (job.completion_percentage || 0) === 0 && (
                  <Button 
                    $variant="warning" 
                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', marginRight: '0.5rem' }}
                    onClick={() => handleRestartJob(job.id)}
                  >
                    Reiniciar
                  </Button>
                )}
                {(job.status === 'running' || job.status === 'pending') && (
                  <Button 
                    $variant="danger" 
                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                    onClick={() => handleCancelSpecificJob(job.id)}
                  >
                    Cancelar
                  </Button>
                )}
                {job.status === 'failed' && (
                  <Button 
                    $variant="warning" 
                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                    onClick={() => handleRestartJob(job.id)}
                  >
                    Tentar Novamente
                  </Button>
                )}
              </div>
            </JobRow>
          ))}
        </JobsTable>
      ) : (
        <div style={{ 
          textAlign: 'center', 
          color: '#666', 
          padding: '3rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '0.5rem',
          border: '2px dashed #dee2e6'
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>📊</div>
          <div style={{ fontSize: '1.125rem', fontWeight: '500', marginBottom: '0.5rem' }}>
            Nenhuma importação realizada ainda
          </div>
          <div style={{ fontSize: '0.875rem', opacity: '0.7' }}>
            Inicie uma nova importação na aba "Importação" para ver o histórico aqui
          </div>
        </div>
      )}
      
      {recentJobs.length > 0 && (
        <div style={{ 
          marginTop: '1.5rem', 
          padding: '1rem', 
          backgroundColor: '#f8f9fa', 
          borderRadius: '0.5rem',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '1rem'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: '600', color: '#007bff' }}>
              {recentJobs.length}
            </div>
            <div style={{ fontSize: '0.875rem', color: '#666' }}>
              Total de Jobs
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: '600', color: '#28a745' }}>
              {recentJobs.filter(job => job.status === 'completed').length}
            </div>
            <div style={{ fontSize: '0.875rem', color: '#666' }}>
              Concluídos
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: '600', color: '#dc3545' }}>
              {recentJobs.filter(job => job.status === 'failed').length}
            </div>
            <div style={{ fontSize: '0.875rem', color: '#666' }}>
              Falharam
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: '600', color: '#ffc107' }}>
              {recentJobs.reduce((sum, job) => sum + (job.created_properties || 0), 0)}
            </div>
            <div style={{ fontSize: '0.875rem', color: '#666' }}>
              Propriedades Criadas
            </div>
          </div>
        </div>
      )}
    </Section>
  );

  const renderSyncTab = () => (
    <>
      <Section>
        <SectionTitle>Sincronização bidirecional</SectionTitle>
        {renderSyncHealth()}
        {syncOverview && (
          <ProgressInfo aria-live="polite" aria-atomic="true">
            <ProgressItem><ProgressLabel>Ativos / limite</ProgressLabel><ProgressValue color={syncOverview.quota.active > syncOverview.quota.limit ? '#dc2626' : undefined}>{syncOverview.quota.active}/{syncOverview.quota.limit}</ProgressValue></ProgressItem>
            <ProgressItem><ProgressLabel>Reservados</ProgressLabel><ProgressValue>{syncOverview.quota.reserved}</ProgressValue></ProgressItem>
            <ProgressItem><ProgressLabel>Vagas livres</ProgressLabel><ProgressValue color="#10b981">{syncOverview.quota.available}</ProgressValue></ProgressItem>
            <ProgressItem><ProgressLabel>Confirmações</ProgressLabel><ProgressValue color={(syncOverview.pending_confirmation || 0) > 0 ? '#d97706' : undefined}>{syncOverview.pending_confirmation || 0}</ProgressValue></ProgressItem>
            <ProgressItem><ProgressLabel>Conflitos</ProgressLabel><ProgressValue color="#f59e0b">{syncOverview.conflicts}</ProgressValue></ProgressItem>
            <ProgressItem><ProgressLabel>Erros</ProgressLabel><ProgressValue color={syncOverview.errors > 0 ? '#dc2626' : undefined}>{syncOverview.errors}</ProgressValue></ProgressItem>
            <ProgressItem><ProgressLabel>Fila</ProgressLabel><ProgressValue>{syncOverview.pending_events}</ProgressValue></ProgressItem>
          </ProgressInfo>
        )}
        <div style={{ display: 'grid', gap: '0.75rem', maxWidth: '680px' }}>
          <label><input type="checkbox" aria-label="Sincronização habilitada" checked={config.sync_enabled} onChange={e => setConfig({...config, sync_enabled: e.target.checked})} /> Sincronização habilitada</label>
          <label><input type="checkbox" aria-label="Receber alterações do Canal Pro" checked={config.inbound_enabled} onChange={e => setConfig({...config, inbound_enabled: e.target.checked})} /> Receber alterações do Canal Pro</label>
          <label><input type="checkbox" aria-label="Enviar alterações do site" checked={config.outbound_enabled} onChange={e => setConfig({...config, outbound_enabled: e.target.checked})} /> Enviar alterações do site</label>
          <FormGroup><Label>Limite de anúncios ativos</Label><Input type="number" min="0" aria-label="Limite de anúncios ativos" value={config.active_listing_limit} onChange={e => setConfig({...config, active_listing_limit: Number(e.target.value)})} /></FormGroup>
          <FormGroup><Label>Intervalo de reconciliação (minutos)</Label><Input type="number" min="5" aria-label="Intervalo de reconciliação em minutos" value={config.sync_interval_minutes} onChange={e => setConfig({...config, sync_interval_minutes: Number(e.target.value)})} /></FormGroup>
          <div>
            <Button onClick={handleSaveSyncSettings} disabled={isLoading}>Salvar controles</Button>
            <Button $variant="secondary" onClick={handleReconcile}>Reconciliar agora</Button>
          </div>
        </div>
      </Section>
      <Section>
        <SectionTitle>Publicação por imóvel</SectionTitle>
        <div style={{ overflowX: 'auto' }}>
          {syncPublications.map(item => (
            <div key={item.id} style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 1fr) 110px 110px 160px 180px', gap: '0.75rem', alignItems: 'center', padding: '0.65rem 0', borderBottom: '1px solid #e5e7eb' }}>
              <div><strong>{item.property_title || item.property_id}</strong><div style={{fontSize: '0.75rem', color: '#666'}}>{item.external_id || 'ainda sem ID externo'}</div></div>
              <div>Atual: {item.actual_status ? channelStatusLabel(item.actual_status) : '-'}</div>
              <div>Desejado: {channelStatusLabel(item.desired_status)}</div>
              <StatusBadge status={item.sync_status} aria-label={`Status: ${syncStatusLabel(item.sync_status) || item.sync_status}`}>{syncStatusLabel(item.sync_status) || item.sync_status}</StatusBadge>
              <div>
                {item.sync_status === 'conflict' ? (
                  <>
                    <Button aria-label={`Usar estado do Canal Pro para ${item.property_title || item.property_id}`} $variant="secondary" style={{fontSize: '0.75rem', padding: '0.35rem'}} onClick={() => handleConflictResolution(item.id, 'remote')}>Usar Canal Pro</Button>
                    <Button aria-label={`Usar estado do site para ${item.property_title || item.property_id}`} style={{fontSize: '0.75rem', padding: '0.35rem'}} onClick={() => handleConflictResolution(item.id, 'local')}>Usar site</Button>
                  </>
                ) : (
                  <>
                    <Button aria-label={`Desativar ${item.property_title || item.property_id} no Canal Pro`} $variant="secondary" style={{fontSize: '0.75rem', padding: '0.35rem'}} onClick={() => handleChannelStatus(item.property_id, 'INACTIVE')}>Desativar</Button>
                    <Button aria-label={`Ativar ${item.property_title || item.property_id} no Canal Pro`} style={{fontSize: '0.75rem', padding: '0.35rem'}} onClick={() => handleChannelStatus(item.property_id, 'ACTIVE')}>Ativar</Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </Section>
    </>
  );

  return (
    <Container>
      <Header>
        <Title>CanalPro</Title>
      </Header>

      {/* Status Card Flutuante */}
      {(isImporting || currentJob) && (
        <ImportStatusCard 
          isActive={activeTab === 'import'}
          onClick={() => setActiveTab('import')}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <strong>Importação em Andamento</strong>
            <span style={{ fontSize: '0.75rem', opacity: 0.7 }}>#{currentJob?.job_id}</span>
          </div>
          <div style={{ fontSize: '0.875rem', marginBottom: '0.5rem' }}>
            {currentJob?.progress_percentage.toFixed(1)}% - {currentJob?.processed_properties}/{currentJob?.total_properties}
          </div>
          <div style={{ fontSize: '0.75rem', opacity: 0.8 }}>
            {currentJob?.current_property && `Processando: ${currentJob.current_property.substring(0, 30)}...`}
          </div>
        </ImportStatusCard>
      )}

      {/* Tabs */}
      <TabsContainer role="tablist" aria-label="Seções do Canal Pro">
        <Tab type="button" role="tab" aria-selected={activeTab === 'import'} $active={activeTab === 'import'} onClick={() => setActiveTab('import')}>
          🚀 Importação
        </Tab>
        <Tab type="button" role="tab" aria-selected={activeTab === 'history'} $active={activeTab === 'history'} onClick={() => setActiveTab('history')}>
          📊 Histórico
        </Tab>
        <Tab type="button" role="tab" aria-selected={activeTab === 'sync'} $active={activeTab === 'sync'} onClick={() => setActiveTab('sync')}>
          🔄 Sincronização
        </Tab>
      </TabsContainer>

      {/* Conteúdo das Tabs */}
      {activeTab === 'import' && renderImportTab()}
      {activeTab === 'history' && renderHistoryTab()}
      {activeTab === 'sync' && renderSyncTab()}

      {/* Modal de Configuração */}
      <Modal
        isOpen={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        title="Configuração CanalPro"
      >
        <div style={{ display: 'grid', gap: '1rem' }}>
          <FormGroup>
            <Label>Email do Usuário</Label>
            <Input
              type="email"
              value={config.user_email}
              onChange={(e) => setConfig({ ...config, user_email: e.target.value })}
              placeholder="seu@email.com"
              autoComplete="username"
            />
          </FormGroup>
          <FormGroup>
            <Label>Senha</Label>
            <Input
              type="password"
              value={config.password}
              onChange={(e) => setConfig({ ...config, password: e.target.value })}
              placeholder="Sua senha"
              autoComplete="current-password"
            />
          </FormGroup>
          <FormGroup>
            <Label>Device ID</Label>
            <Input
              value={config.device_id}
              onChange={(e) => setConfig({ ...config, device_id: e.target.value })}
              placeholder="ID do dispositivo"
            />
          </FormGroup>
          <FormGroup>
            <Label>Publisher ID</Label>
            <Input
              value={config.publisher_id}
              onChange={(e) => setConfig({ ...config, publisher_id: e.target.value })}
              placeholder="ID do publisher"
            />
          </FormGroup>
          <FormGroup>
            <Label>Contract ID</Label>
            <Input
              value={config.contract_id || ''}
              onChange={(e) => setConfig({ ...config, contract_id: e.target.value })}
              placeholder="ID do contrato"
            />
          </FormGroup>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
            <Button $variant="secondary" onClick={() => setShowConfigModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveConfig} disabled={isLoading}>
              {isLoading ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal de Teste de Conexão */}
      <Modal
        isOpen={showTestModal}
        onClose={() => setShowTestModal(false)}
        title="Resultado do Teste de Conexão"
      >
        {testResult && (
          <div>
            <div style={{ 
              padding: '1rem', 
              borderRadius: '0.5rem', 
              backgroundColor: testResult.success ? '#d4edda' : '#f8d7da',
              color: testResult.success ? '#155724' : '#721c24',
              marginBottom: '1rem'
            }}>
              <strong>{testResult.success ? '✅ Sucesso' : '❌ Erro'}</strong>
            </div>
            <p>{testResult.message}</p>
            {testResult.properties_count > 0 && (
              <p><strong>Propriedades encontradas:</strong> {testResult.properties_count}</p>
            )}
          </div>
        )}
      </Modal>

      {/* Modal de Confirmação para Limpar Base */}
      <ConfirmModal
        isOpen={showClearModal}
        onClose={() => setShowClearModal(false)}
        onConfirm={handleClearDatabase}
        title="Limpar Base de Dados"
        message="⚠️ ATENÇÃO: Isso vai deletar TODAS as propriedades e histórico de importação do CanalPro. Esta ação não pode ser desfeita. Tem certeza?"
        confirmText="Sim, Limpar Tudo"
        cancelText="Cancelar"
        type="danger"
      />
    </Container>
  );
};

export default AdminCanalPro; 
